/**
 * Enum names, as a person would read them.
 *
 * The API sends enums by name — `GoodsReceiptNote`, `GstReturn` — because a client comparing
 * against ordinals breaks silently the day one is reordered. That is right on the wire and wrong
 * in a dropdown, so the split happens here rather than in a hand-written label map that has to be
 * extended every time the backend adds a value.
 *
 * Acronyms are the part a naive split gets wrong: "Gst return" reads as a typo to the accountant
 * it is aimed at.
 */

const acronyms = new Set(['GST', 'POS', 'HSN', 'SAC', 'PDC', 'ROI', 'QC', 'GRN', 'FY', 'ID']);

/**
 * @param name The enum *name*, as the API sends it.
 *
 * Defensive about not receiving one, and that is not paranoia — it happened. `JsonStringEnumConverter`
 * emits a bare **number** when a stored value has no matching enum member, which is what a row
 * written with an out-of-range value looks like on the wire. Reached here, `.replace` on a number
 * throws, and because this is called from a table's cell renderer the exception takes the whole
 * screen down: one bad row, no page. A blank cell is a far better failure than that — the row still
 * renders, everything around it still works, and the bad value is visible as a gap rather than
 * hidden behind a stack trace.
 */
export function humanizeEnum(name: string): string {
  if (typeof name !== 'string' || name.length === 0) {
    return '';
  }

  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(' ')
    .filter(Boolean);

  return words
    .map((word, index) => {
      const upper = word.toUpperCase();

      if (acronyms.has(upper)) {
        return upper;
      }

      // Sentence case, not title case: only the first word is capitalised, which is what the rest
      // of the UI does with labels.
      return index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase();
    })
    .join(' ');
}

/**
 * Empty text fields as nulls, on the way to the API.
 *
 * A cleared field means "not set", and sending `""` writes an empty string into a nullable column
 * instead. That is not cosmetic here: unique indexes are filtered on `IsDeleted = 0`, so two
 * branches with `Gstin = ''` collide where two with `NULL` would not, and "has a GSTIN" starts
 * answering yes for records that have nothing.
 *
 * Applied per form rather than inside the client, because for some fields an empty string is the
 * answer — a numbering series with no prefix, or no separator, is a real configuration.
 */
// `T extends object` rather than `Record<string, unknown>`: form value types are interfaces, and
// an interface has no implicit index signature, so the stricter constraint rejects every real
// caller.
export function blankToNull<T extends object>(values: T): T {
  const mapped: Record<string, unknown> = { ...(values as Record<string, unknown>) };

  for (const [key, value] of Object.entries(mapped)) {
    if (typeof value === 'string') {
      const trimmed = value.trim();

      mapped[key] = trimmed === '' ? null : trimmed;
    }
  }

  return mapped as T;
}

/** Options for a select, from a generated enum's value list. */
export function enumOptions<T extends string>(
  values: readonly T[],
): Array<{ label: string; value: T }> {
  return values.map((value) => ({ label: humanizeEnum(value), value }));
}
